// Lending — items that are out with a friend but still yours.
//
// WHY THIS IS NOT A TRADE
// A lend leaves your Steam inventory exactly the way a trade does, so the
// extension records it as a TRADE event with itemsGiven and nothing received,
// and autoLinkTrades stamps the row with a tradeId and an empty tradedFor. That
// is the amber "Resolve" state: the app knowing something left without knowing
// what it was.
//
// But a lend is not a disposal. You still own the item, its cost basis is still
// yours, and it still belongs in your portfolio value. So a lend NEVER writes
// tradeId/tradedFor. It is an overlay on a row that otherwise stays exactly
// where it was:
//
//   row.lend = { id, to, at, dueAt, note, fromTradeId, assetIdOut, float }
//   row.lendHistory = [ ...closed lends, each with returnedAt ]
//
// Because a lent row carries no tradeId, none of the sold/traded predicates in
// SkinTable can see it and no cost-basis splitting is triggered. That is what
// makes this safe to bolt on: the lending layer can only ever add a field.
//
// Steam has no lend concept, so a lend is always a human assertion. What the
// app CAN do is make asserting it free: the counterparty, the timestamp and the
// float all come from the trade history the extension already captured.

import { normalizeTradePartner, LEND_COLOR } from './TradePartner';
import { isTradedAway } from './helpers';

// Re-exported so callers get the whole lending vocabulary from one import. The
// colour itself is defined next to the other two partner badges so the three
// stay a set.
export { LEND_COLOR };

const DAY_MS = 86400000;

const EXTERIOR_IN_NAME = /\s*\((Factory New|Minimal Wear|Field[- ]Tested|Well[- ]Worn|Battle[- ]Scarred)\)\s*$/i;

/** Collapse "Field-Tested" / "Field Tested" / null onto one comparable token. */
function condToken(value) {
  return String(value ?? '').toLowerCase().replace(/[^a-z]/g, '');
}

/** Key a tracker row and an inventory item onto the same string. */
function matchKey(name, condition) {
  const base = String(name ?? '').replace(EXTERIOR_IN_NAME, '').trim().toLowerCase();
  if (!base) return null;
  return `${base}|${condToken(condition)}`;
}

function inventoryKey(item) {
  const mhn = item?.marketHashName || item?.market_hash_name || '';
  const ext = mhn.match(EXTERIOR_IN_NAME);
  return matchKey(mhn, item?.exterior || (ext ? ext[1] : null));
}

/* ─── Row predicates ──────────────────────────────────────── */

/** Currently out with someone. */
export function isLent(row) {
  return !!row?.lend;
}

/**
 * A row that could be lent.
 *
 * Deliberately NOT limited to items still sitting in your Steam inventory. The
 * whole point of recording a lend is that the item is somewhere else, and the
 * commonest thing anyone wants to do on first opening this page is write down
 * loans that already happened. A row in the amber Resolve state — Steam saw it
 * leave, nothing came back — is the single likeliest candidate there is.
 *
 * Sold rows are out because the money already landed. Traded-away rows are out
 * of the default list but recoverable, see canUnwindTrade below.
 */
export function isLendable(row) {
  return !!row?.skin?.value
    && !(parseFloat(row.sellPrice) > 0)
    && !row.lend
    && !isTradedAway(row);
}

/** Where a lendable row's item actually is, for the picker's grouping. */
export const LOCATION = {
  HELD: 'held',     // sitting in your synced Steam inventory
  GONE: 'gone',     // tracked, but not in the inventory any more
  TRADED: 'traded', // recorded as traded away, so lending it means unwinding that
};

/**
 * Which of the three a row is in.
 *
 * "Gone" needs a synced inventory to mean anything: with no sync at all every
 * row would read as gone, which is worse than saying nothing. `hasInventory`
 * lets the caller collapse the distinction rather than assert a falsehood.
 */
export function rowLocation(row, inventoryAssetIds, hasInventory = true) {
  if (isTradedAway(row)) return LOCATION.TRADED;
  if (!hasInventory) return LOCATION.HELD;
  if (row?.assetId && inventoryAssetIds?.has(row.assetId)) return LOCATION.HELD;
  // A row that never carried an assetId (hand-added, bulk-added) cannot be
  // proven either way, so it is not accused of being gone.
  if (!row?.assetId) return LOCATION.HELD;
  return LOCATION.GONE;
}

/**
 * What lending a traded-away row would cost.
 *
 * The row says it was traded for things, and those things became rows of their
 * own carrying a share of its cost basis. Calling it a lend instead means none
 * of that happened, so those rows have to go — otherwise the basis is counted
 * twice and you own items you never received.
 *
 * Unless one of them has been sold. Then there is a realized profit hanging off
 * an item this operation claims never existed, and no honest answer is
 * available: deleting the row would erase real money, keeping it would leave an
 * orphan. So it is refused, and the caller is told why.
 */
export function canUnwindTrade(rows, row) {
  const tradeId = row?.tradeId;
  if (!tradeId) return { ok: true, received: [], sold: [] };

  const received = [];
  const sold = [];
  for (const [i, r] of (rows || []).entries()) {
    if (r?.receivedFromTradeId !== tradeId) continue;
    const entry = { row: r, realIndex: i };
    received.push(entry);
    if (parseFloat(r.sellPrice) > 0) sold.push(entry);
  }
  return { ok: sold.length === 0, received, sold };
}

/**
 * Drop the rows a trade created, so the offered row can become a lend.
 *
 * Returns a fresh array. Call canUnwindTrade first — this one trusts you.
 */
export function unwindTrade(rows, tradeId) {
  if (!tradeId) return rows;
  return (rows || []).filter(r => r.receivedFromTradeId !== tradeId);
}

/** Whole days since it went out. */
export function daysOut(lend) {
  if (!lend?.at) return 0;
  const t = new Date(lend.at).getTime();
  if (Number.isNaN(t)) return 0;
  return Math.max(0, Math.floor((Date.now() - t) / DAY_MS));
}

/* ─── Did it move while it was gone? ──────────────────────── */

/**
 * What the item was worth on the day it went out.
 *
 * Recorded on the lend itself from now on. Everything lent before that, and
 * everything backdated to a day the tracker was not watching, falls back to the
 * daily price snapshots on the row: the last one taken on or before the lend
 * date, or the earliest one there is if the lend predates them all.
 *
 * Returns null rather than guessing when there is nothing to read. A lend with
 * no starting price simply does not get a change figure — an invented baseline
 * would turn ordinary market drift into a number that looks like it means
 * something.
 */
export function lentValue(row, lend) {
  const stored = Number(lend?.valueAt);
  if (Number.isFinite(stored) && stored > 0) return stored;

  const day = typeof lend?.at === 'string' ? lend.at.slice(0, 10) : null;
  if (!day) return null;

  // priceHistory is appended once a day, so it is already in date order.
  const snaps = (row?.priceHistory || []).filter(p => p?.date && p.buffPrice != null);
  if (!snaps.length) return null;

  const before = snaps.filter(p => p.date <= day);
  const pick   = before.length ? before[before.length - 1] : snaps[0];
  const price  = Number(pick.buffPrice);
  return Number.isFinite(price) && price > 0 ? price : null;
}

/**
 * Market movement over the life of the loan.
 *
 * `marketNow` is passed in rather than looked up here so this file stays free
 * of the price map — the caller already has it.
 */
export function lendMove(row, lend, marketNow) {
  const then = lentValue(row, lend);
  if (then == null || marketNow == null) return null;
  const change = marketNow - then;
  return {
    then,
    now: marketNow,
    change,
    pct: then > 0 ? (change / then) * 100 : null,
  };
}

/** Past its expected-back date. Lends with no due date are never overdue. */
export function isOverdue(lend) {
  if (!lend?.dueAt) return false;
  const t = new Date(lend.dueAt).getTime();
  return !Number.isNaN(t) && t < Date.now();
}

/** Days until due — negative once overdue, null when no date was set. */
export function daysUntilDue(lend) {
  if (!lend?.dueAt) return null;
  const t = new Date(lend.dueAt).getTime();
  if (Number.isNaN(t)) return null;
  return Math.ceil((t - Date.now()) / DAY_MS);
}

/* ─── Writing a lend onto a row ───────────────────────────── */

function makeLendId() {
  return 'lend_' + Date.now().toString(36) + '_' + Math.random().toString(36).slice(2, 8);
}

/**
 * Put an item out on loan.
 *
 * Clearing tradeId/tradedFor is the whole point of the trade-side entry: the
 * row arrived here because Steam saw it leave and the app guessed "trade". The
 * user has now said otherwise, so the guess is withdrawn and the row goes back
 * to being an ordinary live holding that happens to be elsewhere. `fromTradeId`
 * keeps the link to the Steam event so nothing is lost by unwinding it.
 */
export function lendRow(row, { to, at, dueAt = null, note = '', valueAt = null }) {
  const partner = normalizeTradePartner(to);
  if (!row || !partner) return row;

  const lend = {
    id:          makeLendId(),
    to:          partner,
    at:          at || new Date().toISOString(),
    dueAt:       dueAt || null,
    note:        note || '',
    fromTradeId: row.tradeId || null,
    assetIdOut:  row.assetId || null,
    // Floats survive the move between accounts; assetIds do not. This is the
    // fingerprint that recognises the item when it comes back.
    float:       row.float ?? null,
    // What it was worth the day it went out, so "did it move while it was
    // gone?" stays answerable. Snapshots only start the day a row is created,
    // so a backdated lend has none to read — see lentValue for that case.
    valueAt:     Number.isFinite(Number(valueAt)) && Number(valueAt) > 0 ? Number(valueAt) : null,
  };

  const next = { ...row, lend };
  // Withdraw the "this was a trade" guess. Both fields go together: a tradeId
  // with no tradedFor is exactly the Resolve state we are leaving.
  delete next.tradeId;
  delete next.tradedFor;
  return next;
}

/**
 * The item came back.
 *
 * The closed lend moves to lendHistory rather than being deleted, so "how often
 * does this person actually return things" stays answerable. `assetId` is the
 * new one Steam issued on the way back in, when the caller knows it.
 */
export function returnRow(row, { at = null, assetId = null } = {}) {
  if (!row?.lend) return row;
  const closed = { ...row.lend, returnedAt: at || new Date().toISOString() };
  const next = {
    ...row,
    lendHistory: [...(row.lendHistory || []), closed],
  };
  if (assetId) next.assetId = assetId;
  delete next.lend;
  return next;
}

/**
 * It is not coming back.
 *
 * Written off as a total loss: the row closes at a sell price of zero, so the
 * money you put in lands in realized P/L as the loss it actually was instead of
 * sitting in your portfolio value forever pretending to be an asset.
 */
export function writeOffRow(row, { at = null, note = '' } = {}) {
  if (!row) return row;
  const when = at || new Date().toISOString();
  const closed = row.lend ? { ...row.lend, returnedAt: null, writtenOffAt: when, note: note || row.lend.note } : null;
  const next = {
    ...row,
    sellPrice:   '0',
    profit:      (-(parseFloat(row.buyPrice) || 0)).toFixed(2),
    soldAt:      when,
    lendWrittenOff: true,
    lendHistory: closed ? [...(row.lendHistory || []), closed] : (row.lendHistory || []),
  };
  delete next.lend;
  return next;
}

/* ─── Who to lend to ──────────────────────────────────────── */

/**
 * Everyone you have traded with, best candidates first.
 *
 * The whole point of reading this off the trade history is that lending to a
 * friend should never involve typing their name. People you trade with often
 * and recently are the people you lend to, so they sort to the top.
 *
 * Events repeat across syncs (the stored history appends without deduping), so
 * trade counts are taken over distinct timestamp+partner pairs. Without that a
 * single trade re-synced thirty times would outrank a real regular.
 */
export function knownFriends({ rows = [], tradeHistory = [] } = {}) {
  const people = new Map();   // key -> record
  const seenTrades = new Set();

  const keyOf = p => p.steamId64 || `name:${p.name.toLowerCase()}`;

  const touch = (partner, when) => {
    const key = keyOf(partner);
    let rec = people.get(key);
    if (!rec) {
      rec = { ...partner, trades: 0, lends: 0, lastAt: null };
      people.set(key, rec);
    }
    // Keep the richest identity we have seen: a later event may carry the
    // profile URL an earlier one lacked.
    if (!rec.profileUrl && partner.profileUrl) rec.profileUrl = partner.profileUrl;
    if (!rec.steamId64 && partner.steamId64) rec.steamId64 = partner.steamId64;
    if (when && (!rec.lastAt || when > rec.lastAt)) rec.lastAt = when;
    return rec;
  };

  for (const event of tradeHistory || []) {
    const partner = normalizeTradePartner(event?.tradedWith);
    if (!partner) continue;
    const rec = touch(partner, event.timestamp || null);
    const sig = `${event.timestamp || ''}|${keyOf(partner)}`;
    if (!seenTrades.has(sig)) {
      seenTrades.add(sig);
      rec.trades += 1;
    }
  }

  // People you have actually lent to, past and present, count double: a proven
  // lending partner is a better guess than someone you traded with once.
  for (const row of rows || []) {
    for (const lend of [row?.lend, ...(row?.lendHistory || [])]) {
      const partner = lend && normalizeTradePartner(lend.to);
      if (!partner) continue;
      const rec = touch(partner, lend.at || null);
      rec.lends += 1;
    }
  }

  return [...people.values()].sort((a, b) => {
    if (b.lends !== a.lends) return b.lends - a.lends;
    if ((b.lastAt || '') !== (a.lastAt || '')) return (b.lastAt || '') < (a.lastAt || '') ? -1 : 1;
    return b.trades - a.trades;
  });
}

/**
 * The counterparty already recorded for a row, if any.
 *
 * This is what makes the trade-side entry one click: the row got here because
 * Steam saw it go to someone, and Steam said who.
 */
export function suggestedPartner(row, tradePartners) {
  if (!row) return null;
  const own = normalizeTradePartner(row.tradedWith);
  if (own) return own;
  const viaIndex = tradePartners?.forRow?.(row);
  return viaIndex ? normalizeTradePartner(viaIndex) : null;
}

/**
 * Partner for an inventory item the Locker is about to lend out.
 *
 * An item still sitting in your inventory has no counterparty of its own, so
 * there is nothing to suggest and the modal falls back to the friend list.
 * Kept as its own function so callers do not have to know that.
 */
export function suggestedPartnerForItem(item, tradePartners) {
  const p = item?.assetId ? tradePartners?.byAssetId?.get(item.assetId) : null;
  return p ? normalizeTradePartner(p) : null;
}

/* ─── Noticing it came back ───────────────────────────────── */

/**
 * Find the returned item in a freshly synced inventory.
 *
 * Float first, and float is close to proof: two copies of the same skin sharing
 * a float to fifteen decimal places is not a coincidence. Only when the lend has
 * no float on record does this fall back to name plus condition, which is a
 * guess, and the caller is told which one it got so it can phrase itself
 * honestly ("came back" versus "looks like it came back").
 *
 * assetId is deliberately not used: Steam issues a new one when an item changes
 * hands, so the id the item left with is the one id it cannot return with.
 */
export function findReturnedItem(lend, inventoryItems, { excludeAssetIds } = {}) {
  if (!lend) return null;
  const skip = excludeAssetIds || new Set();

  if (lend.float != null) {
    const byFloat = (inventoryItems || []).find(i =>
      i?.assetId && !skip.has(i.assetId) &&
      i.float != null && Math.abs(Number(i.float) - Number(lend.float)) < 1e-9);
    if (byFloat) return { item: byFloat, matchedBy: 'float', certain: true };
  }

  const key = lend.itemKey;
  if (!key) return null;
  const byName = (inventoryItems || []).find(i =>
    i?.assetId && !skip.has(i.assetId) && inventoryKey(i) === key);
  return byName ? { item: byName, matchedBy: 'name', certain: false } : null;
}

/**
 * Every open lend, paired with the row it sits on and with the returned item
 * when one is already back in the inventory.
 *
 * One pass, because return matching has to claim inventory items exclusively:
 * two identical lent skins must never both point at the one copy that came back.
 * Certain (float) matches are resolved before guesses so a float match can never
 * lose its item to a name match.
 */
export function openLends({ rows = [], inventoryItems = [] } = {}) {
  const entries = (rows || [])
    .map((row, realIndex) => ({ row, realIndex }))
    .filter(({ row }) => isLent(row))
    .map(e => ({
      ...e,
      lend: {
        ...e.row.lend,
        itemKey: matchKey(e.row.skin?.value, e.row.condition?.value),
      },
    }));

  const claimed = new Set();
  const withFloat = entries.filter(e => e.lend.float != null);
  const rest      = entries.filter(e => e.lend.float == null);

  for (const e of [...withFloat, ...rest]) {
    const hit = findReturnedItem(e.lend, inventoryItems, { excludeAssetIds: claimed });
    if (hit) {
      claimed.add(hit.item.assetId);
      e.back = hit;
    } else {
      e.back = null;
    }
  }

  return entries.sort((a, b) => new Date(b.lend.at || 0) - new Date(a.lend.at || 0));
}

/* ─── Items that only exist in your trade history ─────────── */

/**
 * Things you were given in a trade and never added to the tracker.
 *
 * The tracker only knows what you put in it. Someone who starts tracking in
 * June 2026 has no row for a skin that arrived in February 2025, so the picker
 * — which lists rows — cannot offer it, and there is no way to record that it
 * is out on loan. That was the last hole in "pick the item I lent out".
 *
 * The trade history closes it. Every received item carries a name, usually an
 * exterior, the date it arrived and who sent it: everything a row needs except
 * what you paid, which Steam never records and nobody can infer.
 *
 * Matching is COUNT-AWARE rather than name-based. Receiving three identical
 * cases and tracking one should offer two, not zero and not three, so each
 * existing row consumes one received item.
 */
export function untrackedFromHistory({ rows = [], tradeHistory = [], direction = 'received' } = {}) {
  const sent = direction === 'sent';
  const listOf = e => (sent ? e?.itemsGiven : e?.itemsReceived) || [];

  // How many rows already account for each name+condition.
  const covered = new Map();
  for (const row of rows || []) {
    const key = matchKey(row?.skin?.value, row?.condition?.value);
    if (key) covered.set(key, (covered.get(key) || 0) + 1);
  }

  // Newest first, so the most recent arrival of a duplicate is the one offered.
  const events = [...(tradeHistory || [])]
    .filter(e => listOf(e).length)
    .sort((a, b) => new Date(b.timestamp || 0) - new Date(a.timestamp || 0));

  const seenEvent = new Set();
  const out = [];

  for (const event of events) {
    // The stored history is deduped server-side now, but a client holding an
    // older copy would otherwise show the same arrival several times.
    const sig = `${event.timestamp}|${direction}|${listOf(event).map(i => i?.name).sort().join(',')}`;
    if (seenEvent.has(sig)) continue;
    seenEvent.add(sig);

    const partner = normalizeTradePartner(event.tradedWith);

    for (const item of listOf(event)) {
      const raw = String(item?.name || '').trim();
      if (!raw) continue;

      // Steam's market_hash_name carries the exterior; a row stores the two
      // apart, so split them back out the way the rest of the app expects.
      const extMatch = raw.match(EXTERIOR_IN_NAME);
      const name = raw.replace(EXTERIOR_IN_NAME, '').trim();
      const condition = item.condition || (extMatch ? extMatch[1] : null);

      const key = matchKey(name, condition);
      if (!key) continue;

      // Already accounted for by a real row: consume it and move on.
      const left = covered.get(key) || 0;
      if (left > 0) { covered.set(key, left - 1); continue; }

      out.push({
        key:       `${sig}|${out.length}`,
        name,
        condition: condition || null,
        at:        event.timestamp || null,
        direction,
        // Who the item moved between you and. On a SENT item that is the person
        // holding it right now, which is exactly the borrower the lend modal is
        // about to ask for — so it gets filled in rather than asked.
        partner,
        float:     item.float ?? null,
        paintSeed: item.paintSeed ?? null,
        stickers:  (item.stickers || []).filter(s => s?.name),
      });
    }
  }

  return collapseDuplicateArrivals(out);
}

/**
 * One arrival, one entry.
 *
 * The scraper resolves an item's exterior from the page's description blob when
 * it can and falls back to the bare name when it cannot, so the same arrival can
 * be stored twice — "AWP | Hyper Beast (Minimal Wear)" and "AWP | Hyper Beast".
 * The server collapses those on write now, but a client can be holding history
 * saved before that, and offering the same skin twice in the picker is the kind
 * of thing that makes someone lend one and wonder where the other went.
 *
 * Keyed on name and arrival day, ignoring exterior. The copy that knows the
 * exterior and the sender wins.
 */
function collapseDuplicateArrivals(candidates) {
  const best = new Map();
  const rank = c => (c.condition ? 2 : 0) + (c.partner ? 1 : 0);

  for (const c of candidates) {
    const key = `${c.name.toLowerCase()}|${(c.at || '').slice(0, 10)}|${c.direction}`;
    const prev = best.get(key);
    if (!prev || rank(c) > rank(prev)) best.set(key, c);
  }
  return [...best.values()];
}

/**
 * A tracker row for something that only existed in the trade history.
 *
 * `buyPrice` is whatever the user typed. Blank becomes '0' plus a costUnknown
 * flag rather than a silent zero: a zero cost basis makes the whole sale price
 * read as profit the day it is sold, and a row that quietly lies about that is
 * worse than one that admits it does not know.
 */
export function rowFromHistoryItem(candidate, { buyPrice = '' } = {}) {
  const known = buyPrice !== '' && Number.isFinite(parseFloat(buyPrice));
  return {
    skin:      { value: candidate.name, label: candidate.name },
    condition: candidate.condition ? { value: candidate.condition, label: candidate.condition } : null,
    itemType:  [],
    buyPrice:  known ? parseFloat(buyPrice).toFixed(2) : '0',
    sellPrice: '',
    profit:    '',
    siteFee:   null,
    timestamp: candidate.at || new Date().toISOString(),
    float:     candidate.float ?? null,
    paintSeed: candidate.paintSeed ?? null,
    stickers:  candidate.stickers || [],
    priceHistory: [],
    // Where this row came from, so it is obvious later that it was reconstructed
    // from Steam rather than entered at purchase time.
    fromTradeHistory: true,
    ...(known ? {} : { costUnknown: true }),
    ...(candidate.partner ? { tradedWith: candidate.partner } : {}),
  };
}

/**
 * The borrower to pre-fill for a set of history candidates.
 *
 * Only a SENT item names one: you handed it to that person and they still have
 * it, so they are the borrower. A received item names whoever gave it to you,
 * which says nothing about who you later lent it to, and filling that in would
 * be confidently wrong.
 *
 * Returns null unless every sent candidate agrees on one person, because a
 * pre-filled wrong name is worse than an empty field.
 */
export function borrowerFromCandidates(candidates = []) {
  const sent = candidates.filter(c => c.direction === 'sent' && c.partner);
  if (!sent.length) return null;
  const first = sent[0].partner;
  const id = p => p.steamId64 || p.name;
  return sent.every(c => id(c.partner) === id(first)) ? first : null;
}

/* ─── Searching by person ─────────────────────────────────── */

/**
 * People you have dealt with whose name matches `query`.
 *
 * Two characters minimum: a single letter matches most of a trading history and
 * would turn every keystroke into a full-history view.
 */
export function matchPeople(query, { rows = [], tradeHistory = [] } = {}) {
  const q = String(query || '').trim().toLowerCase();
  if (q.length < 2) return [];
  return knownFriends({ rows, tradeHistory }).filter(p => p.name.toLowerCase().includes(q));
}

/**
 * The person on a row, whichever way they relate to it.
 *
 * A row can name someone in two places: `tradedWith` for the trade that moved
 * it, and `lend.to` for a loan it is currently out on. Searching for a name
 * should find both, so this does not care which one it came from.
 */
export function rowPartnerName(row) {
  const viaTrade = normalizeTradePartner(row?.tradedWith);
  if (viaTrade) return viaTrade.name;
  const viaLend = row?.lend && normalizeTradePartner(row.lend.to);
  return viaLend ? viaLend.name : null;
}

/** True when this row or candidate involves any of `names`. */
export function involvesPerson(partnerName, names) {
  if (!partnerName) return false;
  const lower = partnerName.toLowerCase();
  return names.some(n => n.toLowerCase() === lower);
}

/** Date to default a lend to: the moment it left, when they all left together. */
export function departureFromCandidates(candidates = []) {
  const times = [...new Set(candidates.filter(c => c.direction === 'sent' && c.at).map(c => c.at))];
  return times.length === 1 ? times[0] : null;
}

/** Closed lends across every row, newest first, for the history panel. */
export function closedLends(rows = []) {
  const out = [];
  for (const [realIndex, row] of (rows || []).entries()) {
    for (const lend of row?.lendHistory || []) {
      out.push({ row, realIndex, lend });
    }
  }
  return out.sort((a, b) =>
    new Date(b.lend.returnedAt || b.lend.writtenOffAt || 0) -
    new Date(a.lend.returnedAt || a.lend.writtenOffAt || 0));
}
